import { useCallback } from 'react';
import { useArchiveAsset, useArchiveBoard, useBulkArchive } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { useSelection } from '@/stores/selection';
import { toast } from '@/stores/ui';
import { isApiError } from '@/lib/api/errors';
import { plural } from '@/lib/format';

/**
 * Archive / unarchive with an undo toast (S7a). Shared by the grid context menu, the bulk toolbar,
 * the asset header and the board tree.
 */
export function useTidyActions() {
  const wsId = useSessionStore((s) => s.workspace?.id ?? '');
  const bulk = useBulkArchive(wsId);
  const one = useArchiveAsset();
  const board = useArchiveBoard();

  const archiveAssets = useCallback(
    (ids: string[], archived: boolean) => {
      if (ids.length === 0) return;
      const undo = () => bulk.mutate({ assetIds: ids, archived: !archived }, { onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Undo failed' }) });
      const done = (n: number) => {
        useSelection.getState().clear();
        toast({ kind: 'success', title: archived ? `Archived ${plural(n, 'asset', 'assets')}` : `Restored ${plural(n, 'asset', 'assets')}`, action: { label: 'Undo', onClick: undo }, durationMs: 8000 });
      };
      const fail = (e: unknown) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Archive failed' });
      if (ids.length === 1) one.mutate({ assetId: ids[0]!, archived }, { onSuccess: () => done(1), onError: fail });
      else bulk.mutate({ assetIds: ids, archived }, { onSuccess: (r) => (r.succeeded.length ? done(r.succeeded.length) : toast({ kind: 'warning', title: 'Nothing changed' })), onError: fail });
    },
    [bulk, one],
  );

  const archiveBoard = useCallback(
    (boardId: string, name: string, archived: boolean) => {
      board.mutate(
        { boardId, archived },
        {
          onSuccess: () => toast({ kind: 'success', title: archived ? `Archived “${name}”` : `Restored “${name}”`, body: archived ? 'The board, its sub-boards and assets kept only there are hidden until you show archived items.' : undefined, action: { label: 'Undo', onClick: () => board.mutate({ boardId, archived: !archived }) }, durationMs: 8000 }),
          onError: (e) => toast({ kind: 'error', title: isApiError(e) ? e.message : 'Archive failed' }),
        },
      );
    },
    [board],
  );

  return { archiveAssets, archiveBoard, busy: bulk.isPending || one.isPending || board.isPending };
}
